import { $length, $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { TSDate } from "foundation-ts/tsdate";
import { Nullable, UUID } from "foundation-ts/types";
import { CorporationDto, CreateCorporationBody, CreateUserBody, OrderDto, SubscriptionDto, UpdateCorporationBody, UserDto } from "doxecure-commons/doxecureClientTypes";
import { Consumptions, ConsumptionsStructure, OrdersList } from "doxecure-commons/doxecureTypes";

import apiClient, { assertResponse, g1call } from "@services/apiClient";
import { bytesToDownloadURL } from "@utils/functions";

export const getCorporations = async (): Promise<CorporationDto[]|null> => {
    return await apiClient.getCorporations().then(([ret, status]) => assertResponse(ret, status)) ;
}

export const getCorporation = async (id: UUID): Promise<CorporationDto|null> => {
    return await apiClient.getCorporation(id).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const createCorporation = async (body: CreateCorporationBody): Promise<CorporationDto> => {
    return await apiClient.createCorporation(body).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const updateCorporation = async (id: UUID, body: UpdateCorporationBody): Promise<CorporationDto> => {
    return await apiClient.updateCorporation(id, body).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const deactivateCorporation = async (id: UUID): Promise<boolean> => {
    return await apiClient.deactivateCorporation(id) ;
}

export const reactivateCorporation = async (id: UUID): Promise<boolean> => {
    return await apiClient.reactivateCorporation(id) ;
}

export const getCorporationUsers = async (id: UUID): Promise<UserDto[]|null> => {
    return await apiClient.getCorporationUsers(id).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const createCorporationUser = async (id: UUID, body: CreateUserBody): Promise<UserDto> => {
    return await apiClient.createCorporationUser(id, body).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const getCorporationCurrentConsumption = async (id: UUID): Promise<Nullable<Consumptions>> => {
    const [ret] = await apiClient.corporationCurrentConsumption(id) ;
    return ret ;
}

export const getCorporationConsumptions = async (id: UUID): Promise<Nullable<ConsumptionsStructure>> => {
    const [ret] = await apiClient.corporationConsumptions(id) ;
    return ret ;
}

export const exportCorporationConsumptions = async (id: UUID, corporationName: string): Promise<void> => {
    let url:Nullable<string> = undefined ;
    const ret = await g1call('download csv consumptions', c => c.corporationDownloadConsumptions(id)) ;
    if ($ok(ret)) { const [data] = ret! ; url = bytesToDownloadURL(Buffer.from(data! )) ; }
    if ($length(url) === 0) { 
        TSError.throw("Impossible d'exporter les données") ;
    } else {
        const date = TSDate.fromZulu(new Date()) ;
        const link = document.createElement('a') ;
        link.href = url! ;
        link.download = `consommations_${corporationName}_${date?.toISOString()}.csv`;
        link.click() ;
    }
}

export const createAdminCorporate = async (id: UUID, body: CreateUserBody): Promise<UserDto> => {
    return await apiClient.createAdminCorporate(id, body).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const linkUserToCorporation = async (corporationId: UUID, userId: UUID): Promise<UserDto> => {
    return await apiClient.linkUserToCorporation(corporationId, userId).then(([ret, status]) => assertResponse(ret, status)) ;
}

export const getActiveSubscription = async (corporationId?: UUID): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.getActiveSubscription(corporationId!).then(([ret, status]) => assertResponse(ret, status));
}

export const requestTerminatingSubscription = async (corporationId?: UUID): Promise<boolean> => {
    if (!$ok(corporationId)) { return false; }
    return await apiClient.requestTerminatingSubscription(corporationId!);
}

export const cancelTerminationSubscriptionRequest = async (corporationId?: UUID): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.updateSubscription(corporationId!, { cancelTerminatingSubscription: true }).then(([ret, status]) => assertResponse(ret, status));
}

export const startSubscriptionNow = async (corporationId?: UUID, unlimited?: boolean): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.updateSubscription(corporationId!, { startImmediately: true, upgrateToUnlimited: !!unlimited}).then(([ret, status]) => assertResponse(ret, status));
}

export const buyCreditsPack = async (corporationId?: UUID): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.updateSubscription(corporationId!, { buyCreditsPack: true}).then(([ret, status]) => assertResponse(ret, status));
}

export const upgradeSubscriptionToUnlimited = async (corporationId?: UUID): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.updateSubscription(corporationId!, { upgrateToUnlimited: true}).then(([ret, status]) => assertResponse(ret, status));
}

export const requestDowngradeRenewalToStandardSubscription = async (corporationId?: UUID): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.updateSubscription(corporationId!, { downgradeRenewalToStandard: true}).then(([ret, status]) => assertResponse(ret, status));
}

export const cancelDowngradingRenewalToStandardSubscription = async (corporationId?: UUID): Promise<Nullable<SubscriptionDto>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.updateSubscription(corporationId!, { cancelDowngradingRenewalToStandard: true}).then(([ret, status]) => assertResponse(ret, status));
}

export const fetchOrders = async (corporationId?: UUID): Promise<Nullable<OrderDto[]>> => {
    if (!$ok(corporationId)) { return null; }
    return await apiClient.ordersList(corporationId!).then(([ret, status]) => assertResponse(ret, status));
}

export const downloadInvoicePDF = async (corporationId?: UUID, orderId?: UUID): Promise<Nullable<Buffer>> => {
    if (!$ok(corporationId) || !$ok(orderId)) { return null; }

    return await apiClient.downloadInvoicePDF(corporationId!, orderId!).then(([ret, status]) => assertResponse(ret, status));
}